"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Executor = void 0;
const typed_publisher_1 = require("./typed-publisher");
/**
 * Allows tasks (promises) to be executed with controlled concurrency, providing error-handling ("fail fast"), and notifications
 * that allow consumers to track progress. Uses PQueue as the underlying priority queue.
 *
 * The execution of a task is essentially calling the callback function passed to the copnstructor (`workToDo`), passing
 * to it a `T` value (`T` is a generic type, representing information about a single task).
 *
 * Fail-fast error handling: once a task is rejected, it is guaranteed that no further tasks will be execute. Tasks that
 * started running before the rejection will continue to run. Subsequent calls to `schedule()` are allowed but those
 * scheduled tasks will not be executed.
 *
 * Progress notifications: consumers can use `subscribe()` to register a handler that will be called whenever some
 * progress in made (including progress due to task failures, or tasks that are no-op due to a previous failure).
 */
class Executor {
    queue;
    workToDo;
    firstError;
    publisher = new typed_publisher_1.TypedPublisher();
    constructor(queue, workToDo) {
        this.queue = queue;
        this.workToDo = workToDo;
    }
    /**
     * Schedules a task to be executed. A task will be executed only if no earlier-executed task has failed.
     * @param task information about the task to execute
     */
    schedule(task) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.queue.add(() => this.process(task));
    }
    /**
     * Registers handler to be called when any task settles (either succeeds or fails or is no-op due to an earlier
     * failure). Once handler is registered, it is guaranteed to be called exactly once for every subsequently scheduled
     * task. A handler is not called for previously executed tasks. Handlers are call
     *
     * @param handler the callback function to register
     */
    subscribe(handler) {
        return this.publisher.on('progressMade', handler);
    }
    async process(task) {
        if (!this.firstError) {
            try {
                await this.workToDo(task);
            }
            catch (e) {
                if (!this.firstError) {
                    this.firstError = e;
                }
            }
        }
        // The event that is published intentionally includes an exception, even if it was emitted from the execution of
        // some other vertex. This makes event handling easier on the subscriber side.
        await this.publisher.publish('progressMade', { lastSettled: task, error: this.firstError });
    }
}
exports.Executor = Executor;
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiZXhlY3V0b3IuanMiLCJzb3VyY2VSb290IjoiIiwic291cmNlcyI6WyIuLi8uLi9zcmMvZXhlY3V0b3IudHMiXSwibmFtZXMiOltdLCJtYXBwaW5ncyI6Ijs7O0FBRUEsdURBQWtEO0FBRWxEOzs7Ozs7Ozs7Ozs7O0dBYUc7QUFDSCxNQUFhLFFBQVE7SUFLVTtJQUFnQztJQUpyRCxVQUFVLENBQXFCO0lBRS9CLFNBQVMsR0FBRyxJQUFJLGdDQUFjLEVBQXlELENBQUE7SUFFL0YsWUFBNkIsS0FBYSxFQUFtQixRQUFpQztRQUFqRSxVQUFLLEdBQUwsS0FBSyxDQUFRO1FBQW1CLGFBQVEsR0FBUixRQUFRLENBQXlCO0lBQUcsQ0FBQztJQUVsRzs7O09BR0c7SUFDSCxRQUFRLENBQUMsSUFBTztRQUNkLG1FQUFtRTtRQUNuRSxJQUFJLENBQUMsS0FBSyxDQUFDLEdBQUcsQ0FBQyxHQUFHLEVBQUUsQ0FBQyxJQUFJLENBQUMsT0FBTyxDQUFDLElBQUksQ0FBQyxDQUFDLENBQUE7SUFDMUMsQ0FBQztJQUVEOzs7Ozs7T0FNRztJQUNILFNBQVMsQ0FBQyxPQUEyRDtRQUNuRSxPQUFPLElBQUksQ0FBQyxTQUFTLENBQUMsRUFBRSxDQUFDLGNBQWMsRUFBRSxPQUFPLENBQUMsQ0FBQTtJQUNuRCxDQUFDO0lBRU8sS0FBSyxDQUFDLE9BQU8sQ0FBQyxJQUFPO1FBQzNCLElBQUksQ0FBQyxJQUFJLENBQUMsVUFBVSxFQUFFLENBQUM7WUFDckIsSUFBSSxDQUFDO2dCQUNILE1BQU0sSUFBSSxDQUFDLFFBQVEsQ0FBQyxJQUFJLENBQUMsQ0FBQTtZQUMzQixDQUFDO1lBQUMsT0FBTyxDQUFDLEVBQUUsQ0FBQztnQkFDWCxJQUFJLENBQUMsSUFBSSxDQUFDLFVBQVUsRUFBRSxDQUFDO29CQUNyQixJQUFJLENBQUMsVUFBVSxHQUFHLENBQUMsQ0FBQTtnQkFDckIsQ0FBQztZQUNILENBQUM7UUFDSCxDQUFDO1FBRUQsZ0hBQWdIO1FBQ2hILDhFQUE4RTtRQUM5RSxNQUFNLElBQUksQ0FBQyxTQUFTLENBQUMsT0FBTyxDQUFDLGNBQWMsRUFBRSxFQUFFLFdBQVcsRUFBRSxJQUFJLEVBQUUsS0FBSyxFQUFFLElBQUksQ0FBQyxVQUFVLEVBQUUsQ0FBQyxDQUFBO0lBQzdGLENBQUM7Q0FDRjtBQTFDRCw0QkEwQ0MifQ==